// NEW_FILE_JUSTIFIED: Gmail email page — manages Gmail-specific state (tab, search, pagination, thread selection) and renders EmailList/EmailThread/ComposeEmail
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAction, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: _generated is the only allowed convex import from src/
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw } from 'lucide-react';
import { EmailList } from '@/components/messages/EmailList';
import { EmailThread } from '@/components/messages/EmailThread';
import { ComposeEmail } from '@/components/messages/ComposeEmail';
import { CreateLeadDialog } from '@/components/leads/CreateLeadDialog';
import { ExtractedLeadsDialog } from '@/components/leads/ExtractedLeadsDialog';
import { useGmailMessages } from '@/hooks/useGmailMessages';
import { useExtractedLeads } from '@/hooks/useExtractedLeads';
import { useGmailPageReducer, PAGE_SIZE } from '@/hooks/useGmailPageReducer';
import { useGmailPageLoadingStates } from '@/hooks/useGmailPageLoadingStates';
import { downloadBase64AsFile } from '@/lib/download-utils';
import type { TGmailTab, IComposeEmailData, IGmailCreateLeadState } from '@/@types/gmail';
import type { IAILeadExtras, IMatchedService } from '@/@types/leads';
import type { IOrgServiceType } from '@/@types/serviceTypes';
import type { IInsuranceField } from '@/@types/insurance';

export function GmailPage() {
  const [state, dispatch] = useGmailPageReducer();

  const {
    access,
    emails,
    drafts,
    isLoading,
    totalCount,
    thread,
    sendEmail,
    saveDraft,
    fetchAttachment,
    deleteEmails,
    markThreadRead,
    markEmailsReadStatus,
    syncEmails,
  } = useGmailMessages(state.activeTab, PAGE_SIZE, state.selectedThreadId, state.page);

  const {
    isSyncing,
    setIsSyncing,
    isDeleting,
    setIsDeleting,
    isMarkingReadStatus,
    setIsMarkingReadStatus,
  } = useGmailPageLoadingStates();

  const currentOrg = useQuery(api.organisations.queries.getCurrentOrg);
  const orgServiceTypes = useQuery(
    api.serviceTypes.listByOrg,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const [createLeadState, setCreateLeadState] = useState<IGmailCreateLeadState | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [aiExtras, setAiExtras] = useState<IAILeadExtras | undefined>(undefined);
  const [matchedServices, setMatchedServices] = useState<IMatchedService[] | undefined>(undefined);
  const [unmatchedServices, setUnmatchedServices] = useState<string[] | undefined>(undefined);
  const extractLeadData = useAction(api.leads.aiLeadExtraction.extractLeadData);

  const {
    extractedLeads,
    isExtracting: isMultiExtracting,
    isCreating: isMultiCreating,
    gmailThreadId: extractedThreadId,
    dialogOpen: extractedDialogOpen,
    closeDialog: closeExtractedDialog,
    extractFromThread,
    onLeadCreated,
  } = useExtractedLeads();

  const handleCreateLead = useCallback(
    async (senderName: string, senderEmail: string, subject: string, bodyText?: string) => {
      setIsExtracting(true);
      setAiExtras(undefined);
      setMatchedServices(undefined);
      setUnmatchedServices(undefined);

      try {
        const serviceTypesForAI = orgServiceTypes?.map((st: IOrgServiceType) => ({
          name: st.name,
          requiredFields: st.requiredFields.map((f: IInsuranceField) => ({
            label: f.label,
            fieldType: f.fieldType,
            description: f.description,
            options: f.options,
          })),
        }));

        const result = await extractLeadData({
          type: 'email',
          subject,
          bodyText: bodyText ?? '',
          from: `${senderName} <${senderEmail}>`,
          serviceTypes: serviceTypesForAI,
          defaultCountryCode: '+357',
        });

        setCreateLeadState({
          open: true,
          name: result.name ?? senderName,
          email: result.email ?? senderEmail,
          phone: result.phoneNumber ?? '',
          subject,
        });
        setAiExtras({
          leadType: result.leadType,
          temperature: result.temperature,
          serviceInterest: result.serviceInterest,
          estimatedValue: result.estimatedValue,
          notes: result.notes,
          source: result.source,
        });
        setMatchedServices(result.matchedServices);
        setUnmatchedServices(result.unmatchedServices);
      } catch (error) {
        console.error('AI lead extraction failed, using basic data:', error);
        setCreateLeadState({
          open: true,
          name: senderName,
          email: senderEmail,
          phone: '',
          subject,
        });
      } finally {
        setIsExtracting(false);
      }
    },
    [extractLeadData, orgServiceTypes]
  );

  const handleExtractLeads = useCallback(
    (threadData: {
      threadId: string;
      subject: string;
      messages: Array<{
        from: string;
        bodyText?: string;
        snippet: string;
        direction: 'incoming' | 'outgoing';
      }>;
    }) => {
      void extractFromThread(threadData);
    },
    [extractFromThread]
  );

  // Handlers
  const handleTabChange = useCallback(
    (tab: TGmailTab) => {
      dispatch({ type: 'SET_TAB', payload: tab });
    },
    [dispatch]
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      dispatch({ type: 'SET_SEARCH', payload: query });
    },
    [dispatch]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch({ type: 'SET_PAGE', payload: page });
    },
    [dispatch]
  );

  const handleSelectionChange = useCallback(
    (ids: Set<string>) => {
      dispatch({ type: 'SET_SELECTED_IDS', payload: ids });
    },
    [dispatch]
  );

  const handleEmailClick = useCallback(
    (threadId: string) => {
      dispatch({ type: 'SET_THREAD', payload: threadId });
      if (access.canWrite) {
        markThreadRead(threadId).catch(() => {
          // Best-effort
        });
      }
    },
    [markThreadRead, dispatch, access.canWrite]
  );

  const handleBackFromThread = useCallback(() => {
    dispatch({ type: 'SET_THREAD', payload: null });
  }, [dispatch]);

  const handleCompose = useCallback(() => {
    dispatch({ type: 'SET_COMPOSING', payload: true });
  }, [dispatch]);

  const handleBackFromCompose = useCallback(() => {
    dispatch({ type: 'SET_COMPOSING', payload: false });
  }, [dispatch]);

  const handleDelete = useCallback(
    (ids: string[]) => {
      const gmailMessageIds = emails.filter(e => ids.includes(e.id)).map(e => e.gmailMessageId);

      if (gmailMessageIds.length === 0) return;

      setIsDeleting(true);
      deleteEmails(gmailMessageIds)
        .then(() => {
          toast.success(`${gmailMessageIds.length} email(s) moved to trash`);
          dispatch({ type: 'SET_SELECTED_IDS', payload: new Set() });
        })
        .catch(() => {
          toast.error('Failed to delete emails');
        })
        .finally(() => {
          setIsDeleting(false);
        });
    },
    [emails, deleteEmails, dispatch, setIsDeleting]
  );

  const handleMarkReadStatus = useCallback(
    (ids: string[], markAsRead: boolean) => {
      const gmailMessageIds = emails.filter(e => ids.includes(e.id)).map(e => e.gmailMessageId);

      if (gmailMessageIds.length === 0) return;

      setIsMarkingReadStatus(true);
      markEmailsReadStatus(gmailMessageIds, markAsRead)
        .then(() => {
          toast.success(
            markAsRead
              ? `${gmailMessageIds.length} email(s) marked as read`
              : `${gmailMessageIds.length} email(s) marked as unread`
          );
          dispatch({ type: 'SET_SELECTED_IDS', payload: new Set() });
        })
        .catch(() => {
          toast.error('Failed to update read status');
        })
        .finally(() => {
          setIsMarkingReadStatus(false);
        });
    },
    [emails, markEmailsReadStatus, dispatch, setIsMarkingReadStatus]
  );

  // Type adapters: sendEmail/saveDraft return Promise<{...}> but ComposeEmail expects Promise<void>
  const handleSend = useCallback(
    (data: IComposeEmailData): Promise<void> =>
      sendEmail(data, state.replyThreadId ?? undefined, state.replyInReplyTo ?? undefined).then(
        () => undefined
      ),
    [sendEmail, state.replyThreadId, state.replyInReplyTo]
  );

  const handleSaveDraft = useCallback(
    (data: IComposeEmailData): Promise<void> => saveDraft(data).then(() => undefined),
    [saveDraft]
  );

  const handleReply = useCallback(() => {
    if (thread) {
      const lastMessage = thread.messages[thread.messages.length - 1];
      if (lastMessage) {
        dispatch({
          type: 'SET_REPLY',
          payload: {
            data: {
              to: [lastMessage.from],
              subject: thread.subject.startsWith('Re:') ? thread.subject : `Re: ${thread.subject}`,
            },
            threadId: thread.threadId,
            inReplyTo: lastMessage.gmailMessageId,
          },
        });
        return;
      }
    }
    dispatch({ type: 'SET_COMPOSING', payload: true });
  }, [thread, dispatch]);

  const threadContactInfo = useMemo(() => {
    if (!thread) return null;
    const incoming = thread.messages.find(m => m.direction === 'incoming');
    const msg = incoming ?? thread.messages[0];
    if (!msg) return null;
    const emailMatch = msg.from.match(/<([^>]+)>/);
    const email = emailMatch ? emailMatch[1] : msg.from;
    const nameMatch = msg.from.match(/^([^<]+)</);
    const name = nameMatch ? nameMatch[1].trim() : (msg.from.split('@')[0] ?? '');
    return { email, name };
  }, [thread]);

  const handleEditAndSendDraft = useCallback(
    (draftText: string, subject: string) => {
      if (!thread || !threadContactInfo) return;
      const lastMessage = thread.messages[thread.messages.length - 1];
      dispatch({
        type: 'SET_REPLY',
        payload: {
          data: {
            to: [threadContactInfo.email],
            subject,
            bodyHtml: `<p>${draftText.replace(/\n/g, '<br/>')}</p>`,
          },
          threadId: thread.threadId,
          inReplyTo: lastMessage?.gmailMessageId ?? '',
        },
      });
    },
    [thread, threadContactInfo, dispatch]
  );

  const handleForward = useCallback(() => {
    if (thread) {
      const lastMessage = thread.messages[thread.messages.length - 1];
      dispatch({
        type: 'SET_REPLY',
        payload: {
          data: {
            to: [],
            subject: thread.subject.startsWith('Fwd:') ? thread.subject : `Fwd: ${thread.subject}`,
          },
          threadId: thread.threadId,
          inReplyTo: lastMessage?.gmailMessageId ?? '',
        },
      });
      return;
    }
    dispatch({ type: 'SET_COMPOSING', payload: true });
  }, [thread, dispatch]);

  const handleSync = useCallback(() => {
    setIsSyncing(true);
    syncEmails()
      .then(() => {
        toast.success('Emails synced');
      })
      .catch(() => {
        toast.error('Failed to sync emails');
      })
      .finally(() => {
        setIsSyncing(false);
      });
  }, [syncEmails, setIsSyncing]);

  const handleDownloadAttachment = useCallback(
    (messageId: string, attachmentId: string, filename: string) => {
      fetchAttachment(messageId, attachmentId)
        .then(result => {
          downloadBase64AsFile(result.data, result.mimeType, filename);
        })
        .catch(() => {
          toast.error('Failed to download attachment');
        });
    },
    [fetchAttachment]
  );

  // Lead creation dialog + extraction overlay (shared across thread and list views)
  const leadDialog = (
    <>
      {createLeadState && (
        <CreateLeadDialog
          open={createLeadState.open}
          onOpenChange={open => {
            if (!open) {
              setCreateLeadState(null);
              setAiExtras(undefined);
              setMatchedServices(undefined);
              setUnmatchedServices(undefined);
            }
          }}
          prefillName={createLeadState.name}
          prefillEmail={createLeadState.email}
          prefillPhone={createLeadState.phone}
          prefillSubject={createLeadState.subject}
          prefillSource={aiExtras?.source}
          aiExtras={aiExtras}
          matchedServices={matchedServices}
          unmatchedServices={unmatchedServices}
          orgServiceTypes={orgServiceTypes?.map((st: IOrgServiceType) => ({
            _id: st._id,
            orgId: st.orgId,
            name: st.name,
            requiredFieldCount: st.requiredFields.length,
          }))}
          onCreated={() => {
            setMatchedServices(undefined);
            setUnmatchedServices(undefined);
          }}
        />
      )}
      {(isExtracting || isMultiExtracting) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/50">
          <div className="flex items-center gap-3 rounded-xl bg-card border border-border px-6 py-4 shadow-lg">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm font-medium text-foreground">Analyzing email...</span>
          </div>
        </div>
      )}
      <ExtractedLeadsDialog
        open={extractedDialogOpen}
        onOpenChange={open => {
          if (!open) closeExtractedDialog();
        }}
        leads={extractedLeads}
        gmailThreadId={extractedThreadId ?? ''}
        orgServiceTypes={orgServiceTypes?.map((st: IOrgServiceType) => ({
          _id: st._id,
          orgId: st.orgId,
          name: st.name,
          requiredFieldCount: st.requiredFields.length,
        }))}
        onLeadCreated={onLeadCreated}
        isCreating={isMultiCreating}
      />
    </>
  );

  // Pagination info
  const pagination = useMemo(
    () => ({ page: state.page, pageSize: PAGE_SIZE, total: totalCount }),
    [state.page, totalCount]
  );

  // Reply context is captured in the reducer before thread is cleared

  // Not connected state — neither an own session nor a shared org mailbox
  if (!access.hasOwnSession && access.sharedEmailAddress === null && !isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
        <p className="text-sm">Gmail is not connected.</p>
        <p className="text-xs">
          <Link to="/connected-accounts" className="text-primary underline underline-offset-4">
            Go to Connected Accounts
          </Link>{' '}
          to connect your Gmail.
        </p>
      </div>
    );
  }

  // Banner shown throughout the shared (read-only) mailbox experience.
  // role="status" overrides Alert's default role="alert": this is persistent
  // informational text that remounts on every list/thread navigation, so an
  // assertive region would re-interrupt screen readers each time.
  const sharedMailboxBanner =
    access.sharedEmailAddress !== null ? (
      <Alert role="status" className="mx-4 mt-3 shrink-0">
        <AlertDescription>
          Viewing shared mailbox{' '}
          <span className="font-medium text-foreground">{access.sharedEmailAddress}</span> —
          read-only
        </AlertDescription>
      </Alert>
    ) : null;

  // Thread view
  if (state.selectedThreadId !== null && thread !== null) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {sharedMailboxBanner}
        <div className="flex-1 overflow-auto">
          <EmailThread
            thread={thread}
            onBack={handleBackFromThread}
            onReply={handleReply}
            onForward={handleForward}
            onDownloadAttachment={handleDownloadAttachment}
            onCreateLead={handleCreateLead}
            contactIdentifier={threadContactInfo?.email}
            contactName={threadContactInfo?.name}
            onEditAndSendDraft={handleEditAndSendDraft}
            onExtractLeads={handleExtractLeads}
            canWrite={access.canWrite}
          />
        </div>
        {leadDialog}
      </div>
    );
  }

  // Compose view — unreachable for read-only viewers even via a direct
  // /messages/gmail/compose navigation, since it falls through to the list view below.
  if (state.isComposing && access.canWrite) {
    return (
      <ComposeEmail
        onBack={handleBackFromCompose}
        onSend={handleSend}
        onSaveDraft={handleSaveDraft}
        initialData={state.replyContext ?? undefined}
      />
    );
  }

  // Email list view
  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <h1 className="text-lg font-semibold text-foreground">Gmail</h1>
        <div className="flex items-center gap-2">
          {access.canWrite && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSync}
                disabled={isSyncing}
                aria-label="Refresh emails"
              >
                <RefreshCw
                  size={16}
                  className={isSyncing ? 'animate-spin' : ''}
                  aria-hidden="true"
                />
              </Button>
              <Button size="sm" onClick={handleCompose}>
                Compose
              </Button>
            </>
          )}
        </div>
      </div>

      {sharedMailboxBanner}

      {/* Email List */}
      <EmailList
        emails={emails}
        drafts={drafts}
        activeTab={state.activeTab}
        onTabChange={handleTabChange}
        searchQuery={state.searchQuery}
        onSearchChange={handleSearchChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        selectedIds={state.selectedIds}
        onSelectionChange={handleSelectionChange}
        onEmailClick={handleEmailClick}
        onDelete={handleDelete}
        isDeleting={isDeleting}
        onMarkReadStatus={handleMarkReadStatus}
        isMarkingReadStatus={isMarkingReadStatus}
        isLoading={isLoading}
        onCreateLead={handleCreateLead}
        canWrite={access.canWrite}
      />

      {leadDialog}
    </div>
  );
}
